import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Center,
  Stack,
  Spinner
} from '@chakra-ui/react';
import { MergeModel, ArtistModel } from 'models';
import { ProcessStatus } from 'models/types';
import DetailedMergeCard from '../DetailedMergeCard/DetailedMergeCard';
import MergeFiltersForm from '../../Form/MergeFiltersForm/MergeFiltersForm';
import EmptyState from '../../EmptyState/EmptyState';
import PaginationFooter from '../../Pagination/PaginationFooter/PaginationFooter';
import { filterItemsByQuery } from 'utils/search';

const PER_PAGE = 10;

export interface DetailedMergeGridProps {
  merges: MergeModel[];
  isLoading: boolean;
  onApprove?: (mergeId: string) => void;
  onReject?: (mergeId: string) => void;
  approveLoading?: boolean;
  rejectLoading?: boolean;
  defaultStatus?: ProcessStatus;
  onStatusChange?: (status: ProcessStatus) => void;
  defaultAccordionOpen?: boolean;
  availableArtists?: ArtistModel[];
  selectionMode?: boolean;
  selectedMerges?: MergeModel[];
  onMergeSelect?: (merge: MergeModel) => void;
  onFilteredMergesChange?: (merges: MergeModel[]) => void;
}

export default function DetailedMergeGrid({
  merges,
  isLoading,
  onApprove,
  onReject,
  approveLoading = false,
  rejectLoading = false,
  defaultStatus = ProcessStatus.PENDING,
  onStatusChange,
  defaultAccordionOpen = false,
  availableArtists = [],
  selectionMode = false,
  selectedMerges = [],
  onMergeSelect,
  onFilteredMergesChange
}: DetailedMergeGridProps) {
  const [selectedStatus, setSelectedStatus] = useState<ProcessStatus>(defaultStatus);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedArtists, setSelectedArtists] = useState<ArtistModel[]>([]);
  const [page, setPage] = useState(1);

  const filteredMerges = useMemo(() => {
    let filtered = merges;

    // Filter by artists
    if (selectedArtists.length > 0) {
      const artistIds = new Set(selectedArtists.map(artist => artist.id));
      filtered = filtered.filter(merge => {
        return Array.from(artistIds).some(artistId => merge.containsArtist(artistId));
      });
    }

    // Filter by search query
    if (searchQuery?.trim()) {
      // 1) Collect the merged items from filtered merges
      const items = filtered.map(m => m.mergedItem);

      // 2) Reuse your util to filter items
      const filteredItems = filterItemsByQuery(items, searchQuery);

      // 3) Build a quick lookup and filter merges
      const allowedIds = new Set(filteredItems.map(item => item.id));
      filtered = filtered.filter(m => allowedIds.has(m.mergedItem.id));
    }

    return filtered;
  }, [merges, searchQuery, selectedArtists]);

  // Memoize paginated merges
  const paginatedMerges = useMemo(() => {
    const startIdx = (page - 1) * PER_PAGE;
    const endIdx = startIdx + PER_PAGE;
    return filteredMerges.slice(startIdx, endIdx);
  }, [filteredMerges, page]);

  useEffect(() => {
    onFilteredMergesChange?.(paginatedMerges);
  }, [paginatedMerges, onFilteredMergesChange]);

  // Calculate total pages based on filtered results
  const filteredTotalPages = Math.ceil(filteredMerges.length / PER_PAGE);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleStatusChange = (status: ProcessStatus) => {
    setSelectedStatus(status);
    setPage(1); // Reset to first page when status changes
    onStatusChange?.(status);
  };

  const handleQueryChange = (query: string) => {
    setSearchQuery(query);
    setPage(1); // Reset to first page when search changes
  };

  const handleArtistChange = (artists: ArtistModel[]) => {
    setSelectedArtists(artists);
    setPage(1); // Reset to first page when artists change
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedArtists([]);
    setPage(1);
  };

  const isOnFilterMode = (): boolean => {
    return searchQuery.length > 0 || selectedArtists.length > 0;
  };

  const renderMergeCards = () => {
    if (isLoading) {
      return (
        <Center p={8}>
          <Spinner size="xl" />
        </Center>
      );
    }

    if (!filteredMerges?.length) {
      return (
        <EmptyState
          header="No Merges"
          description="No Results Found"
          showButton={isOnFilterMode()}
          onButtonClick={handleClearFilters}
          buttonText="Clear Filters"
        />
      );
    }

    return (
      <Stack spacing={4}>
        {paginatedMerges.map(merge => (
          <DetailedMergeCard
            key={`merge_card_${merge.id}`}
            merge={merge}
            onApprove={onApprove}
            onReject={onReject}
            approveLoading={approveLoading}
            rejectLoading={rejectLoading}
            defaultAccordionOpen={defaultAccordionOpen}
            selectionMode={selectionMode}
            selected={selectedMerges.some(m => m.id === merge.id)}
            onSelect={() => onMergeSelect?.(merge)}
          />
        ))}
      </Stack>
    );
  };

  return (
    <Box width="100%">
      <Box mb={6}>
        <MergeFiltersForm
          defaultStatus={selectedStatus}
          defaultQuery={searchQuery}
          defaultArtists={selectedArtists}
          availableArtists={availableArtists}
          onStatusChange={handleStatusChange}
          onQueryChange={handleQueryChange}
          onArtistChange={handleArtistChange}
        />
      </Box>
      {renderMergeCards()}
      {filteredTotalPages > 1 && (
        <Box mt={8}>
          <PaginationFooter
            currentPage={page}
            lastPage={filteredTotalPages}
            siblingsCount={1}
            onPageChange={handlePageChange}
          />
        </Box>
      )}
    </Box>
  );
}
